// src/pages/Public/CRM/PublicLeadForm.js

import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
    Box, Paper, Typography, Button, TextField, 
    CircularProgress, Alert, Container, Grid
} from '@mui/material';
import { CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import axios from 'axios';
import backendURL from '../../../../config';

const PublicLeadForm = () => {
    const { formUuid } = useParams();
    
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [formData, setFormData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});

    const fetchForm = useCallback(async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${backendURL}/api/public/lead-form/${formUuid}`);
            
            if (response.data.success) {
                setForm(response.data.data);
                // Initialize form data
                const initialData = {};
                response.data.data.fields_config?.forEach(field => {
                    initialData[field.field] = '';
                });
                setFormData(initialData);
            } else {
                setError(response.data.message || 'Form not found');
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Unable to load form');
        } finally {
            setLoading(false);
        }
    }, [formUuid]);

    useEffect(() => {
        if (formUuid) {
            fetchForm();
        }
    }, [fetchForm, formUuid]);

    // Capture UTM parameters
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        setFormData(prev => ({
            ...prev,
            utm_source: urlParams.get('utm_source') || '',
            utm_medium: urlParams.get('utm_medium') || '',
            utm_campaign: urlParams.get('utm_campaign') || '',
            referrer_url: document.referrer || '',
        }));
    }, []);

    const handleChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
        if (validationErrors[field]) {
            setValidationErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors[field];
                return newErrors;
            });
        }
    };

    const validate = () => {
        const errors = {};
        form.fields_config?.forEach(field => {
            if (field.required && !formData[field.field]?.trim()) {
                errors[field.field] = `${field.label} is required`;
            }
            if (field.type === 'email' && formData[field.field] && 
                !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData[field.field])) {
                errors[field.field] = 'Invalid email format';
            }
        });
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validate()) return;

        setSubmitting(true);
        try {
            const response = await axios.post(
                `${backendURL}/api/public/lead-form/${formUuid}/submit`,
                formData
            );
            
            if (response.data.success) {
                setSubmitted(true);
                if (response.data.redirect_url) {
                    setTimeout(() => {
                        window.location.href = response.data.redirect_url;
                    }, 2000);
                }
            } else {
                setError(response.data.message);
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to submit form');
        } finally {
            setSubmitting(false);
        }
    };

    const renderField = (fieldConfig) => {
        const { field, label, type, required, placeholder } = fieldConfig;
        const hasError = !!validationErrors[field];

        switch (type) {
            case 'textarea':
                return (
                    <TextField
                        key={field}
                        fullWidth
                        multiline
                        rows={4}
                        label={label}
                        value={formData[field] || ''}
                        onChange={handleChange(field)}
                        error={hasError}
                        helperText={validationErrors[field]}
                        required={required}
                        placeholder={placeholder}
                        sx={{ mb: 2 }}
                    />
                );
            case 'email':
                return (
                    <TextField
                        key={field}
                        fullWidth
                        type="email"
                        label={label}
                        value={formData[field] || ''}
                        onChange={handleChange(field)}
                        error={hasError}
                        helperText={validationErrors[field]}
                        required={required}
                        placeholder={placeholder}
                        sx={{ mb: 2 }}
                    />
                );
            case 'tel':
                return (
                    <TextField
                        key={field}
                        fullWidth
                        type="tel"
                        label={label}
                        value={formData[field] || ''}
                        onChange={handleChange(field)}
                        error={hasError}
                        helperText={validationErrors[field]}
                        required={required}
                        placeholder={placeholder}
                        sx={{ mb: 2 }}
                    />
                );
            default:
                return (
                    <TextField
                        key={field}
                        fullWidth
                        label={label}
                        value={formData[field] || ''}
                        onChange={handleChange(field)}
                        error={hasError}
                        helperText={validationErrors[field]}
                        required={required}
                        placeholder={placeholder}
                        sx={{ mb: 2 }}
                    />
                );
        }
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    if (error && !form) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh" p={2}>
                <Paper sx={{ p: 4, maxWidth: 500, textAlign: 'center' }}>
                    <ErrorIcon sx={{ fontSize: 60, color: 'error.main', mb: 2 }} />
                    <Typography variant="h5" gutterBottom>Form Unavailable</Typography>
                    <Typography color="text.secondary">{error}</Typography>
                </Paper>
            </Box>
        );
    }

    if (submitted) {
        return (
            <Box 
                display="flex" 
                justifyContent="center" 
                alignItems="center" 
                minHeight="100vh" 
                bgcolor={form?.background_color || '#f5f5f5'}
                p={2}
            >
                <Paper sx={{ p: 4, maxWidth: 600, textAlign: 'center' }}>
                    <CheckCircle sx={{ fontSize: 80, color: 'success.main', mb: 2 }} />
                    <Typography variant="h4" gutterBottom>Thank You!</Typography>
                    <Typography variant="body1" color="text.secondary">
                        {form?.thank_you_message || 'Your information has been submitted successfully.'}
                    </Typography>
                </Paper>
            </Box>
        );
    }

    return (
        <Box 
            minHeight="100vh" 
            bgcolor={form?.background_color || '#f5f5f5'} 
            py={4}
        >
            <Container maxWidth="sm">
                <Paper 
                    sx={{ 
                        p: 4, 
                        borderTop: `6px solid ${form?.primary_color || '#1976d2'}` 
                    }}
                >
                    {form?.logo_url && (
                        <Box mb={3} textAlign="center">
                            <img 
                                src={form.logo_url} 
                                alt="Logo" 
                                style={{ maxHeight: 80, maxWidth: '100%' }} 
                            />
                        </Box>
                    )}

                    <Typography variant="h4" gutterBottom fontWeight="bold">
                        {form?.title || 'Contact Us'}
                    </Typography>

                    {form?.description && (
                        <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                            {form.description}
                        </Typography>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                            {error}
                        </Alert>
                    )}

                    <form onSubmit={handleSubmit}>
                        {form?.fields_config?.map(renderField)}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            size="large"
                            disabled={submitting}
                            sx={{ 
                                mt: 2,
                                bgcolor: form?.primary_color || 'primary.main',
                                '&:hover': {
                                    bgcolor: form?.primary_color 
                                        ? `${form.primary_color}dd` 
                                        : 'primary.dark'
                                }
                            }}
                        >
                            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
                        </Button>
                    </form>
                </Paper>

                <Box textAlign="center" py={2}>
                    <Typography variant="caption" color="text.secondary">
                        Powered by KlonIT CRM
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default PublicLeadForm;